"use client";

import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { NewsletterLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import { Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import {
  ALL_NEWSLETTER_ISSUES,
  CURRENT_NEWSLETTER,
  getAdjacentNewsletters,
  getNewsletterIssue,
  newsletterHref,
  type NewsletterIssue,
} from "./newsletterContent";

const pageFont =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const navLinkSx = {
  fontFamily: pageFont,
  fontSize: "0.8125rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.45)",
  "&:hover": { color: "#E8FF4D" },
};

function Paragraphs({ text, color }: { text: string; color: string }) {
  return (
    <>
      {text
        .split(/\n\n+/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => (
          <Typography
            key={p.slice(0, 40)}
            sx={{
              fontFamily: pageFont,
              color,
              fontSize: { xs: "1rem", md: "1.0625rem" },
              lineHeight: 1.7,
              mb: 2,
              whiteSpace: "pre-line",
              "&:last-child": { mb: 0 },
            }}
          >
            {p}
          </Typography>
        ))}
    </>
  );
}

function SectionLabel({
  children,
  color = "rgba(255,255,255,0.72)",
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <Typography
      sx={{
        fontFamily: pageFont,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color,
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

function Title({
  children,
  component = "h2",
  color = "#fff",
  size = "section",
}: {
  children: React.ReactNode;
  component?: "h1" | "h2";
  color?: string;
  size?: "hero" | "section";
}) {
  return (
    <Typography
      component={component}
      sx={{
        fontFamily: pageFont,
        fontWeight: 700,
        color,
        fontSize:
          size === "hero"
            ? { xs: "2.5rem", md: "3.75rem" }
            : { xs: "1.75rem", md: "2.25rem" },
        letterSpacing: size === "hero" ? "-0.03em" : "-0.02em",
        lineHeight: size === "hero" ? 1.05 : 1.15,
        mb: size === "hero" ? 2 : 3,
      }}
    >
      {children}
    </Typography>
  );
}

function NewsletterIssueView({ issue }: { issue: NewsletterIssue }) {
  const isLatest = issue.slug === CURRENT_NEWSLETTER.slug;
  const { older, newer } = getAdjacentNewsletters(issue.slug);
  const otherIssues = ALL_NEWSLETTER_ISSUES.filter((item) => item.slug !== issue.slug);

  return (
    <>
      <NewsletterLayoutHelmet issue={issue} />
      <Header />
      <Box
        component="main"
        sx={{
          bgcolor: "#050505",
          color: "#fff",
          fontFamily: pageFont,
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: 760,
            mx: "auto",
            px: { xs: 2.5, md: 4 },
            pt: { xs: 6, md: 9 },
            pb: { xs: 5, md: 7 },
            textAlign: "center",
          }}
        >
          {isLatest ? (
            <Box
              sx={{
                position: { xs: "relative", md: "absolute" },
                top: { md: 28 },
                left: { md: 16 },
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: { xs: 2.5, md: 0 },
                px: 1.5,
                py: 0.85,
                borderRadius: "999px",
                bgcolor: "#E8FF4D",
                color: "#0D0D0D",
                boxShadow: "4px 4px 0 rgba(255,255,255,0.2)",
                transform: { xs: "none", md: "rotate(-6deg)" },
                maxWidth: { xs: "100%", md: 200 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: pageFont,
                  fontSize: { xs: "0.6875rem", md: "0.75rem" },
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  lineHeight: 1.25,
                  textAlign: "center",
                }}
              >
                New edition dropping every Thursday!
              </Typography>
            </Box>
          ) : null}
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
              mb: 1,
            }}
          >
            {issue.tagline}
          </Typography>
          {issue.edition ? (
            <Typography
              sx={{
                fontFamily: pageFont,
                fontSize: "0.8125rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#fff",
                mb: 2,
              }}
            >
              {issue.edition}
            </Typography>
          ) : null}

          {(older || newer) && (
            <Box
              component="nav"
              aria-label="Edition navigation"
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                mb: 3,
              }}
            >
              {older ? (
                <Link component={RouterLink} to={newsletterHref(older)} underline="hover" sx={navLinkSx}>
                  ← Older
                </Link>
              ) : (
                <Box component="span" sx={{ ...navLinkSx, color: "rgba(255,255,255,0.2)" }}>
                  ← Older
                </Box>
              )}
              {newer ? (
                <Link component={RouterLink} to={newsletterHref(newer)} underline="hover" sx={navLinkSx}>
                  Newer →
                </Link>
              ) : (
                <Box component="span" sx={{ ...navLinkSx, color: "rgba(255,255,255,0.2)" }}>
                  Newer →
                </Box>
              )}
            </Box>
          )}
          <SectionLabel color="rgba(255,255,255,0.72)">{issue.heroLabel}</SectionLabel>
          <Title component="h1" size="hero" color="#fff">
            {issue.heroTitle}
          </Title>
          <Typography
            sx={{
              fontFamily: pageFont,
              color: "rgba(255,255,255,0.72)",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.55,
              maxWidth: 620,
              mx: "auto",
            }}
          >
            {issue.heroSummary}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: "#F3F3F3", color: "#111" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <SectionLabel color="#666">{issue.noteLabel}</SectionLabel>
            <Paragraphs text={issue.noteBody} color="#272727" />
          </Box>
        </Box>

        <Box sx={{ bgcolor: "#0D0D0D" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <SectionLabel>{issue.comic.label}</SectionLabel>
            <Title color="#fff">{issue.comic.title}</Title>
            {issue.comic.image ? (
              <Box
                component="img"
                src={issue.comic.image}
                alt={issue.comic.imageAlt}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  display: "block",
                  mb: 2,
                  aspectRatio: "16 / 10",
                  objectFit: "cover",
                  objectPosition: "center top",
                  bgcolor: "#171717",
                }}
              />
            ) : null}
            <Typography
              sx={{
                fontFamily: pageFont,
                color: "#fff",
                fontWeight: 600,
                mb: 2,
                fontSize: "1.0625rem",
              }}
            >
              {issue.comic.caption}
            </Typography>
            <Paragraphs text={issue.comic.body} color="rgba(255,255,255,0.72)" />
            {issue.comic.ctaHref ? (
              <Button
                href={issue.comic.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mt: 3,
                  bgcolor: "#EDEDED",
                  color: "#151515",
                  fontFamily: pageFont,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderRadius: "999px",
                  px: 3,
                  py: 1.25,
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                {issue.comic.ctaLabel}
              </Button>
            ) : issue.comic.ctaLabel ? (
              <Typography
                sx={{
                  mt: 3,
                  fontFamily: pageFont,
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.72)",
                }}
              >
                {issue.comic.ctaLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ bgcolor: "#F3F3F3", color: "#111" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <SectionLabel color="#666">{issue.updates.label}</SectionLabel>
            <Title color="#111">{issue.updates.title}</Title>
            {issue.updates.image ? (
              <Box
                component="img"
                src={issue.updates.image}
                alt={issue.updates.imageAlt || ""}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  display: "block",
                  mb: 3,
                  aspectRatio: "16 / 10",
                  objectFit: "cover",
                  bgcolor: "#e5e5e5",
                }}
              />
            ) : null}
            <Paragraphs text={issue.updates.body} color="#222" />
          </Box>
        </Box>

        <Box sx={{ bgcolor: "#0D0D0D" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <SectionLabel color="#B59CFF">{issue.magic.label}</SectionLabel>
            <Title color="#fff">{issue.magic.title}</Title>
            {issue.magic.image ? (
              <>
                <Box
                  component="img"
                  src={issue.magic.image}
                  alt={issue.magic.imageAlt}
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    display: "block",
                    mb: issue.magic.imageCredit ? 1 : 3,
                    aspectRatio: "16 / 10",
                    objectFit: "cover",
                    bgcolor: "#171717",
                  }}
                />
                {issue.magic.imageCredit ? (
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      color: "rgba(255,255,255,0.5)",
                      fontSize: "0.75rem",
                      lineHeight: 1.4,
                      mb: 3,
                    }}
                  >
                    {issue.magic.imageCredit}
                  </Typography>
                ) : null}
              </>
            ) : null}
            <Paragraphs text={issue.magic.body} color="rgba(255,255,255,0.78)" />
          </Box>
        </Box>

        <Box sx={{ bgcolor: "#0D0D0D", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <SectionLabel>{issue.story.label}</SectionLabel>
            <Title color="#fff">{issue.story.title}</Title>
            {issue.story.image ? (
              <Box
                component="img"
                src={issue.story.image}
                alt={issue.story.imageAlt}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  display: "block",
                  mb: 3,
                  aspectRatio: "16 / 10",
                  objectFit: "cover",
                  bgcolor: "#171717",
                }}
              />
            ) : null}
            <Paragraphs text={issue.story.body} color="rgba(255,255,255,0.78)" />
          </Box>
        </Box>

        {issue.dream.title ? (
          <Box sx={{ bgcolor: "#101010" }}>
            <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
              <SectionLabel>{issue.dream.label}</SectionLabel>
              <Title color="#fff">{issue.dream.title}</Title>
              <Paragraphs text={issue.dream.intro} color="rgba(255,255,255,0.7)" />
              <Box sx={{ mt: 3, mb: 3 }}>
                <Paragraphs text={issue.dream.body} color="#fff" />
              </Box>
              <Link
                href="mailto:david@comedyuo.com"
                underline="hover"
                sx={{
                  fontFamily: pageFont,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "1.0625rem",
                }}
              >
                {issue.dream.contribute}
              </Link>
            </Box>
          </Box>
        ) : null}

        <Box sx={{ bgcolor: "#101010", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
            <Paragraphs text={issue.signoff} color="#fff" />
            {issue.footerImage ? (
              <>
                <Box
                  component="img"
                  src={issue.footerImage}
                  alt={issue.footerImageAlt}
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    display: "block",
                    mt: 4,
                    mb: 2,
                    aspectRatio: "16 / 10",
                    objectFit: "cover",
                    bgcolor: "#000",
                  }}
                />
                {issue.footerCaption ? (
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      color: "rgba(255,255,255,0.5)",
                      fontSize: "0.9375rem",
                      lineHeight: 1.55,
                    }}
                  >
                    {issue.footerCaption}
                  </Typography>
                ) : null}
              </>
            ) : null}

            {otherIssues.length > 0 ? (
              <Box
                component="nav"
                aria-label="Previous editions"
                sx={{
                  mt: 5,
                  pt: 4,
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <SectionLabel>{isLatest ? "Previous editions" : "Other editions"}</SectionLabel>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {otherIssues.map((item) => (
                    <Link
                      key={item.slug}
                      component={RouterLink}
                      to={newsletterHref(item)}
                      underline="none"
                      sx={{
                        fontFamily: pageFont,
                        color: "rgba(255,255,255,0.78)",
                        fontSize: { xs: "1rem", md: "1.0625rem" },
                        fontWeight: 600,
                        py: 1.5,
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "color 0.15s ease",
                        "&:hover": { color: "#E8FF4D" },
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      {item.edition}
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          color: "rgba(255,255,255,0.35)",
                          fontSize: "1.125rem",
                          fontWeight: 400,
                          ml: 2,
                        }}
                      >
                        →
                      </Box>
                    </Link>
                  ))}
                </Box>
              </Box>
            ) : null}

            <Box
              component="nav"
              aria-label="More from ComedyUO"
              sx={{
                mt: 5,
                pt: 3,
                borderTop: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1.5, md: 2.5 },
              }}
            >
              {[
                { label: "Get tickets", href: "/tickets" },
                { label: "Magic UO", href: "/magic" },
                { label: "Press", href: "/press" },
                { label: "About", href: "/about" },
              ].map((item) => (
                <Link
                  key={item.href}
                  component={RouterLink}
                  to={item.href}
                  underline="hover"
                  sx={{
                    fontFamily: pageFont,
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
}

export default function NewsletterPage() {
  const { slug } = useParams<{ slug?: string }>();

  if (!slug) {
    return <NewsletterIssueView issue={CURRENT_NEWSLETTER} />;
  }

  if (slug === CURRENT_NEWSLETTER.slug) {
    return <Navigate to="/newsletter" replace />;
  }

  const issue = getNewsletterIssue(slug);
  if (!issue) {
    return <Navigate to="/newsletter" replace />;
  }

  return <NewsletterIssueView issue={issue} />;
}
